// Zadávací pole podle dema (P5): jedno pole, Enter odesílá, Shift+Enter nový řádek.
// Při delším textu se plynule přeskládá (text na celou šířku, „+“ a odeslání pod něj), přepnutí
// se počítá z naměřené šířky textu (canvas) a animuje technikou FLIP.
// Přílohy jako v demu: „+“ (výběr souboru), Ctrl+V (obrázek nebo soubor ze schránky), přetažení na stránku.
// Soubor se nahraje hned po přidání; odeslat jde, až jsou všechny nahrané.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUpIcon, PlusIcon } from '@phosphor-icons/react';
import { useReducedMotion, useToast } from '@smworks-cz/ui-kit';
import { ATTACHMENT_LIMITS, type AttachmentDTO } from '@sam/shared';
import { AttachmentTile } from './Attachment';
import { pastedName, uploadFile } from '../lib/files';

export interface ComposerHandle {
  focus(): void;
  rect(): DOMRect | null;
  el(): HTMLDivElement | null;
}

interface Props {
  placeholder: string;
  disabled?: boolean;
  busy?: boolean;
  onSend: (text: string, attachments: AttachmentDTO[]) => Promise<boolean> | boolean;
  autoFocus?: boolean;
}

interface Draft {
  key: string;
  name: string;
  mime: string;
  size: number;
  preview: string | null;
  uploaded: AttachmentDTO | null;
  error: string | null;
  abort: AbortController;
}

const EASE = '0.26s cubic-bezier(0.2, 0.8, 0.2, 1)';
const MAX_H = 220;

export const Composer = forwardRef<ComposerHandle, Props>(function Composer({ placeholder, disabled, busy, onSend, autoFocus }, ref) {
  const box = useRef<HTMLDivElement>(null);
  const ta = useRef<HTMLTextAreaElement>(null);
  const send = useRef<HTMLButtonElement>(null);
  const plus = useRef<HTMLButtonElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [multi, setMulti] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [drag, setDrag] = useState(false);
  const reduce = useReducedMotion();
  const { toast } = useToast();
  const canvas = useRef<CanvasRenderingContext2D | null>(null);
  const flip = useRef<{ rects: DOMRect[]; h: number } | null>(null);
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  useImperativeHandle(ref, () => ({
    focus: () => ta.current?.focus(),
    rect: () => box.current?.getBoundingClientRect() ?? null,
    el: () => box.current,
  }));

  useEffect(() => {
    if (autoFocus && window.innerWidth > 920) ta.current?.focus();
  }, [autoFocus]);

  // Uklidit náhledy a rozběhnutá nahrávání při odchodu.
  useEffect(
    () => () => {
      for (const d of draftsRef.current) {
        d.abort.abort();
        if (d.preview) URL.revokeObjectURL(d.preview);
      }
    },
    [],
  );

  /* ---------- přílohy ---------- */

  const addFiles = useCallback(
    (list: FileList | File[]) => {
      const files = Array.from(list);
      if (!files.length || disabled) return;
      const room = ATTACHMENT_LIMITS.maxFiles - draftsRef.current.length;
      if (files.length > room) toast({ variant: 'warning', title: `Nejvýš ${ATTACHMENT_LIMITS.maxFiles} příloh na zprávu.` });
      const add: Draft[] = [];
      for (const raw of files.slice(0, Math.max(0, room))) {
        if (raw.size > ATTACHMENT_LIMITS.maxBytes) {
          toast({ variant: 'error', title: `Soubor ${raw.name} je větší než 20 MB.` });
          continue;
        }
        const name = pastedName(raw);
        const file = name === raw.name ? raw : new File([raw], name, { type: raw.type });
        const d: Draft = {
          key: crypto.randomUUID(),
          name,
          mime: file.type || 'application/octet-stream',
          size: file.size,
          preview: /^image\/(png|jpe?g|gif|webp)$/.test(file.type) ? URL.createObjectURL(file) : null,
          uploaded: null,
          error: null,
          abort: new AbortController(),
        };
        add.push(d);
        uploadFile(file, d.abort.signal)
          .then((up) => setDrafts((all) => all.map((x) => (x.key === d.key ? { ...x, uploaded: up } : x))))
          .catch((e) => {
            if (d.abort.signal.aborted) return;
            setDrafts((all) => all.map((x) => (x.key === d.key ? { ...x, error: (e as Error).message } : x)));
          });
      }
      if (add.length) {
        setDrafts((all) => [...all, ...add]);
        ta.current?.focus();
      }
    },
    [disabled, toast],
  );

  const removeDraft = (key: string) => {
    setDrafts((all) => {
      const d = all.find((x) => x.key === key);
      if (d) {
        d.abort.abort();
        if (d.preview) URL.revokeObjectURL(d.preview);
      }
      return all.filter((x) => x.key !== key);
    });
  };

  // Ctrl+V kdekoli na stránce (mimo modální okna) a přetažení souboru na stránku.
  useEffect(() => {
    const isInModal = (t: EventTarget | null) => t instanceof Element && !!t.closest('[role="dialog"]');
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    let depth = 0;
    const onPaste = (e: ClipboardEvent) => {
      if (isInModal(e.target)) return;
      const files = e.clipboardData?.files;
      if (files?.length) {
        e.preventDefault();
        addFiles(files);
      }
    };
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth++;
      setDrag(true);
    };
    const onOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setDrag(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDrag(false);
      if (!isInModal(e.target) && e.dataTransfer?.files.length) addFiles(e.dataTransfer.files);
    };
    document.addEventListener('paste', onPaste);
    document.addEventListener('dragenter', onEnter);
    document.addEventListener('dragover', onOver);
    document.addEventListener('dragleave', onLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('dragenter', onEnter);
      document.removeEventListener('dragover', onOver);
      document.removeEventListener('dragleave', onLeave);
      document.removeEventListener('drop', onDrop);
    };
  }, [addFiles]);

  /* ---------- přeskládání ---------- */

  const fit = useCallback(() => {
    const t = ta.current;
    if (!t) return;
    t.style.height = 'auto';
    t.style.height = `${Math.min(t.scrollHeight, MAX_H)}px`;
  }, []);

  const textWidth = (v: string) => {
    const t = ta.current!;
    if (!canvas.current) canvas.current = document.createElement('canvas').getContext('2d');
    const cs = getComputedStyle(t);
    const c = canvas.current!;
    c.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    return v.split('\n').reduce((m, l) => Math.max(m, c.measureText(l).width), 0);
  };

  const setMultiAnimated = (on: boolean) => {
    if (on === multi) return;
    if (!reduce && box.current && ta.current && send.current && plus.current) {
      flip.current = { rects: [plus.current, ta.current, send.current].map((e) => e.getBoundingClientRect()), h: box.current.getBoundingClientRect().height };
    }
    setMulti(on);
  };

  /** Rozhodne, jestli má být pole jednořádkové, nebo rozšířené. Zpět s rezervou, ať na hranici nebliká. */
  const relayout = useCallback(
    (v: string) => {
      if (!box.current || !ta.current) return;
      if (!v) {
        setMultiAnimated(false);
        return;
      }
      const inner = box.current.getBoundingClientRect().width - 2 - 16 - 38 - 38 - 12 - 12;
      const nl = v.includes('\n');
      const w = textWidth(v);
      if (!multi) {
        if (nl || w > inner - 4) setMultiAnimated(true);
      } else if (!nl && w < inner - 48) setMultiAnimated(false);
      else fit();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multi, fit],
  );

  // FLIP: po přepnutí třídy změřit nové pozice a animovat z původních.
  useLayoutEffect(() => {
    const t = ta.current!;
    if (multi) fit();
    else t.style.height = '';
    const f = flip.current;
    flip.current = null;
    if (!f || !box.current || !send.current || !plus.current) return;
    const els = [plus.current, t, send.current];
    const last = els.map((e) => e.getBoundingClientRect());
    const h1 = box.current.getBoundingClientRect().height;
    els.forEach((e, i) => {
      e.style.transition = 'none';
      e.style.transform = `translate(${f.rects[i].left - last[i].left}px, ${f.rects[i].top - last[i].top}px)`;
    });
    const b = box.current;
    b.style.transition = 'none';
    b.style.height = `${f.h}px`;
    void b.offsetHeight;
    const raf = requestAnimationFrame(() => {
      els.forEach((e) => {
        e.style.transition = `transform ${EASE}`;
        e.style.transform = '';
      });
      b.style.transition = `height ${EASE}, border-color 0.15s`;
      b.style.height = `${h1}px`;
    });
    const to = setTimeout(() => {
      els.forEach((e) => {
        e.style.transition = '';
        e.style.transform = '';
      });
      b.style.transition = '';
      b.style.height = '';
    }, 300);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(to);
    };
  }, [multi, fit]);

  useEffect(() => {
    const onResize = () => relayout(ta.current?.value ?? '');
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [relayout]);

  /* ---------- odeslání ---------- */

  const uploading = drafts.some((d) => !d.uploaded && !d.error);
  const failed = drafts.some((d) => d.error);
  const ready = drafts.filter((d) => d.uploaded).map((d) => d.uploaded!);

  const submit = async () => {
    const text = value.trim();
    if ((!text && !ready.length) || disabled || busy || uploading || failed) return;
    const prevValue = value;
    const prevDrafts = drafts;
    setValue('');
    setDrafts([]);
    relayout('');
    const ok = await onSend(text, ready);
    if (!ok) {
      setValue(prevValue);
      setDrafts(prevDrafts);
      requestAnimationFrame(() => relayout(prevValue));
    } else {
      for (const d of prevDrafts) if (d.preview) URL.revokeObjectURL(d.preview);
    }
  };

  const can = (!!value.trim() || ready.length > 0) && !uploading && !failed;
  return (
    <div ref={box} className={`cmp${multi ? ' multi' : ''}${disabled ? ' is-disabled' : ''}${drag ? ' drag' : ''}`}>
      {drafts.length > 0 && (
        <div className="cmp-files">
          {drafts.map((d) => (
            <AttachmentTile
              key={d.key}
              name={d.name}
              mime={d.mime}
              size={d.size}
              src={d.preview}
              uploading={!d.uploaded && !d.error}
              error={d.error}
              onRemove={() => removeDraft(d.key)}
            />
          ))}
        </div>
      )}
      <button
        ref={plus}
        type="button"
        className="plus"
        aria-label="Přiložit soubor"
        title="Přiložit soubor (nebo vložte Ctrl+V či přetáhněte)"
        disabled={disabled || drafts.length >= ATTACHMENT_LIMITS.maxFiles}
        onClick={() => picker.current?.click()}
      >
        <PlusIcon size={20} />
      </button>
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <textarea
        ref={ta}
        rows={1}
        value={value}
        placeholder={placeholder}
        aria-label="Zpráva pro Sama"
        disabled={disabled}
        onChange={(e) => {
          setValue(e.target.value);
          relayout(e.target.value);
          if (multi) fit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void submit();
          }
        }}
      />
      <button
        ref={send}
        type="button"
        className="send"
        aria-label="Odeslat"
        title={busy ? 'Sam ještě odpovídá' : uploading ? 'Nahrávám přílohy…' : failed ? 'Odeberte přílohu, která se nenahrála' : 'Odeslat (Enter)'}
        disabled={!can || disabled || busy}
        onClick={() => void submit()}
      >
        <ArrowUpIcon size={20} weight="bold" />
      </button>
    </div>
  );
});
